/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from 'react'
import { useTheme } from 'styled-components'
import {
  FaFileExcel,
  FaTable,
  FaBuilding,
  FaTimes,
  FaSpinner,
  FaCalendarAlt,
} from 'react-icons/fa'
import { TbRadar2 } from 'react-icons/tb'
import { MdOutlineRadar } from 'react-icons/md'
import { toast } from 'react-toastify'
import {
  Container,
  HeaderSection,
  ExportButton,
  TabsContainer,
  TabButton,
  ContentCard,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from './styles'
import api from '../../services/axios'
import { useMonthFilter } from '../../context/FiltersContext/MonthFilter'
import { useGeralFilter } from '../../context/FiltersContext/PeriodoFilter'
import { intervaloDoMes, normalizeDateForComparison, formatDateBr } from '../../constantes'
import { Skeleton } from '../../components/Skeletron'
import { SkuKpisGrid } from './SkuKpisGrid'
import { SkuNavalBattleChart } from './SkuNavalBattleChart'
import { SkuHeatmapMatrix } from './SkuHeatmapMatrix'
import { SkuDistributorMatrix } from './SkuDistributorMatrix'
import { SkuMediaAnaliseResponse } from './types'
import { exportSkuAnalyticsToExcel } from './exportExcel'
import { carregarDistribuidores } from '../../services/dists'

export default function SkuAnalyticsScreen() {
  const theme = useTheme() as any
  const isLight = theme?.name === 'light'
  const { month: selectedMonth, year: selectedYear, distributorId } = useMonthFilter()
  const {
    year: gfYear,
    month: gfMonth,
    endMonth: gfEndMonth,
    startDate: gfStartDate,
    endDate: gfEndDate,
    distributorId: gfDistId,
    distributorIds: gfDistIds,
    marca: gfMarca,
    categoria: gfCategoria,
    operacao: gfOperacao,
    uf: gfUf,
  } = useGeralFilter()

  const [activeTab, setActiveTab] = useState<'batalha' | 'tabela' | 'distribuidores'>('batalha')
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<SkuMediaAnaliseResponse | null>(null)

  // Estados do Modal de Exportação com Escolha de Distribuidor
  const [openExportModal, setOpenExportModal] = useState(false)
  const [exportDistId, setExportDistId] = useState<string>('todos')
  const [exporting, setExporting] = useState(false)
  const [distributorsList, setDistributorsList] = useState<
    Array<{ id: string | number; name: string; regional?: string }>
  >([])

  // Carrega a lista completa de distribuidores para o modal de exportação
  useEffect(() => {
    const fetchDists = async () => {
      try {
        const raw = await carregarDistribuidores()
        const map = new Map<string, { id: string | number; name: string; regional?: string }>()

        if (Array.isArray(raw)) {
          raw.forEach((d: any) => {
            const id = String(d.id || d.cod_dist || d._id)
            const name = d.fantasy_name || d.name || d.distribuidor || d.nome || `Distribuidor #${id}`
            map.set(id, { id, name, regional: d.regional || '' })
          })
        }

        if (data?.distribuidores) {
          data.distribuidores.forEach((d) => {
            const id = String(d.distId)
            if (!map.has(id)) {
              map.set(id, { id, name: d.distribuidor, regional: d.regional || '' })
            }
          })
        }

        const sorted = Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name))
        setDistributorsList(sorted)
      } catch (err) {
        console.error('Erro ao carregar distribuidores para exportação:', err)
      }
    }
    fetchDists()
  }, [data])

  const { effectiveStartDate, effectiveEndDate, periodLabel } = useMemo(() => {
    const meses = [
      'Janeiro',
      'Fevereiro',
      'Março',
      'Abril',
      'Maio',
      'Junho',
      'Julho',
      'Agosto',
      'Setembro',
      'Outubro',
      'Novembro',
      'Dezembro',
    ]

    if (gfStartDate && gfEndDate) {
      return {
        effectiveStartDate: normalizeDateForComparison(gfStartDate),
        effectiveEndDate: normalizeDateForComparison(gfEndDate),
        periodLabel: `${formatDateBr(gfStartDate)} a ${formatDateBr(gfEndDate)}`,
      }
    }

    if (gfStartDate && !gfEndDate) {
      const dStr = normalizeDateForComparison(gfStartDate)
      return {
        effectiveStartDate: dStr,
        effectiveEndDate: dStr,
        periodLabel: formatDateBr(gfStartDate),
      }
    }

    const y = gfYear || selectedYear || new Date().getFullYear()
    const m =
      gfMonth !== undefined && gfMonth !== null
        ? gfMonth
        : selectedMonth !== undefined && selectedMonth !== null
        ? selectedMonth
        : new Date().getMonth()
    const mEnd =
      gfEndMonth !== undefined && gfEndMonth !== null && gfEndMonth >= m
        ? gfEndMonth
        : m

    const { from } = intervaloDoMes(y, m)
    const { to } = intervaloDoMes(y, mEnd)
    const label = m === mEnd ? `${meses[m]} de ${y}` : `${meses[m]} a ${meses[mEnd]} de ${y}`

    return {
      effectiveStartDate: from,
      effectiveEndDate: to,
      periodLabel: label,
    }
  }, [gfStartDate, gfEndDate, gfYear, gfMonth, gfEndMonth, selectedYear, selectedMonth])

  const activeDistIds = useMemo(() => {
    if (gfDistIds && gfDistIds.length > 0) return gfDistIds.map(String)
    if (gfDistId && gfDistId !== 'todos') return [String(gfDistId)]
    if (distributorId && distributorId !== 'todos') return [String(distributorId)]
    return []
  }, [gfDistIds, gfDistId, distributorId])

  const load = async () => {
    if (!effectiveStartDate || !effectiveEndDate) return

    setLoading(true)
    try {
      const payload: any = {
        startDate: effectiveStartDate,
        endDate: effectiveEndDate,
        distId:
          activeDistIds.length === 1
            ? activeDistIds[0]
            : activeDistIds.length > 1
            ? activeDistIds.join(',')
            : 'todos',
        distributorIds: activeDistIds,
        marca: gfMarca ? [gfMarca] : undefined,
        categoria: gfCategoria ? [gfCategoria] : undefined,
        regional: gfOperacao ? [gfOperacao] : undefined,
        operacao: gfOperacao ? [gfOperacao] : undefined,
        uf: gfUf ? [gfUf] : undefined,
      }

      const res = await api.post('/sku/media-analise', payload, { timeout: 300000 })
      if (res.data?.success) {
        setData(res.data)
      } else {
        console.warn('Falha ao carregar análise de SKUs:', res.data?.error)
        setData(null)
      }
    } catch (err) {
      console.error('Erro na requisição de análise de SKU:', err)
      setData(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    effectiveStartDate,
    effectiveEndDate,
    activeDistIds.join(','),
    gfMarca,
    gfCategoria,
    gfOperacao,
    gfUf,
  ])

  const selectedDistributorName = useMemo(() => {
    if (activeDistIds.length === 0) return 'Todos os Distribuidores (Nacional)'
    if (activeDistIds.length === 1) {
      const dist = data?.distribuidores?.find((d) => String(d.distId) === activeDistIds[0])
      return dist ? dist.distribuidor : `Distribuidor #${activeDistIds[0]}`
    }
    return `${activeDistIds.length} Distribuidores Selecionados`
  }, [activeDistIds, data])

  const handleOpenExportModal = () => {
    setExportDistId(distributorId ? String(distributorId) : 'todos')
    setOpenExportModal(true)
  }

  const handleConfirmExport = async () => {
    if (!data || selectedMonth === undefined || !selectedYear) return

    setExporting(true)
    try {
      const currentDist = distributorId ? String(distributorId) : 'todos'

      // Se for o mesmo distribuidor da tela atual, exporta diretamente
      if (exportDistId === currentDist) {
        exportSkuAnalyticsToExcel(data, periodLabel, selectedDistributorName, gfUf || undefined)
        toast.success('Planilha Excel gerada com sucesso!')
        setOpenExportModal(false)
        return
      }

      // Se for um distribuidor diferente ou 'todos', busca na API os dados específicos
      const { from, to } = intervaloDoMes(selectedYear, selectedMonth)
      const payload = {
        startDate: from,
        endDate: to,
        distId: exportDistId === 'todos' ? undefined : exportDistId,
        marca: gfMarca ? [gfMarca] : undefined,
        categoria: gfCategoria ? [gfCategoria] : undefined,
        operacao: gfOperacao ? [gfOperacao] : undefined,
        uf: gfUf ? [gfUf] : undefined,
      }

      const res = await api.post('/sku/media-analise', payload, { timeout: 300000 })
      if (res.data?.success) {
        let distName = 'Todos os Distribuidores (Nacional)'
        if (exportDistId !== 'todos') {
          const found = distributorsList.find((d) => String(d.id) === String(exportDistId))
          distName = found ? found.name : `Distribuidor #${exportDistId}`
        }

        exportSkuAnalyticsToExcel(res.data, periodLabel, distName, gfUf || undefined)
        toast.success(`Planilha gerada com sucesso para ${distName}!`)
        setOpenExportModal(false)
      } else {
        toast.error('Não foi possível obter dados para este distribuidor.')
      }
    } catch (err) {
      console.error('Erro ao exportar planilha de SKU:', err)
      toast.error('Erro ao gerar a planilha. Verifique a conexão e tente novamente.')
    } finally {
      setExporting(false)
    }
  }

  return (
    <Container>
      <HeaderSection>
        <div className='title-group'>
          <div className='icon-wrapper'>
            <TbRadar2 />
          </div>
          <div>
            <h1>Inteligência de SKUs & Batalha Naval</h1>
            <p>
              Análise de sortimento médio, produtividade por item e matriz estratégica 4 quadrantes &bull;{' '}
              <strong style={{ color: isLight ? '#0f172a' : '#e2e8f0' }}>{periodLabel}</strong>
              {gfUf && (
                <span
                  style={{
                    marginLeft: 8,
                    padding: '2px 8px',
                    borderRadius: '12px',
                    backgroundColor: '#a6c10b',
                    color: '#001D53',
                    fontWeight: 'bold',
                    fontSize: '11px',
                    display: 'inline-block',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                  }}
                >
                  UF: {gfUf}
                </span>
              )}
            </p>
          </div>
        </div>

        <div className='actions-group'>
          <ExportButton
            type='button'
            onClick={handleOpenExportModal}
            disabled={loading || !data}
            style={{ opacity: loading || !data ? 0.5 : 1 }}
            title='Escolher distribuidor e exportar relatório executivo em Excel'
          >
            <FaFileExcel />
            Exportar Relatório XLS
          </ExportButton>
        </div>
      </HeaderSection>

      {loading ? (
        <Skeleton variant='dashboard' />
      ) : data ? (
        <>
          <SkuKpisGrid kpis={data.kpis} />

          <TabsContainer>
            <TabButton
              type='button'
              $active={activeTab === 'batalha'}
              onClick={() => setActiveTab('batalha')}
            >
              <MdOutlineRadar size={16} />
              Batalha Naval (Matriz 4 Quadrantes)
            </TabButton>
            <TabButton
              type='button'
              $active={activeTab === 'tabela'}
              onClick={() => setActiveTab('tabela')}
            >
              <FaTable size={14} />
              Visão Tabular (Drill-Down Cat -&gt; Subcat -&gt; SKU)
            </TabButton>
            <TabButton
              type='button'
              $active={activeTab === 'distribuidores'}
              onClick={() => setActiveTab('distribuidores')}
            >
              <FaBuilding size={14} />
              Sortimento por Distribuidor ({data.distribuidores?.length || 0})
            </TabButton>
          </TabsContainer>

          {activeTab === 'batalha' && (
            <SkuNavalBattleChart
              categorias={data.batalhaNaval.categorias}
              subcategorias={data.batalhaNaval.subcategorias}
              marcas={data.batalhaNaval.marcas}
              familias={data.batalhaNaval.familias}
              corteCat={data.batalhaNaval.linhasDeCorteCategorias}
              corteSub={data.batalhaNaval.linhasDeCorteSubcategorias}
              corteMarca={data.batalhaNaval.linhasDeCorteMarcas}
              corteFam={data.batalhaNaval.linhasDeCorteFamilias}
              distributorId={
                activeDistIds.length === 1
                  ? activeDistIds[0]
                  : activeDistIds.length > 1
                  ? activeDistIds.join(',')
                  : undefined
              }
              periodLabel={periodLabel}
              selectedDistributorName={selectedDistributorName}
              startDate={data.periodo?.startDate}
              endDate={data.periodo?.endDate}
              uf={gfUf || undefined}
              rankingUfs={data.rankingUfs}
            />
          )}

          {activeTab === 'tabela' && (
            <SkuHeatmapMatrix hierarquia={data.hierarquia} />
          )}

          {activeTab === 'distribuidores' && (
            <SkuDistributorMatrix distribuidores={data.distribuidores} />
          )}
        </>
      ) : (
        <ContentCard>
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
            <TbRadar2 size={48} color='#64748b' style={{ marginBottom: 12 }} />
            <h3 style={{ color: isLight ? '#0f172a' : '#fff', margin: '0 0 8px 0' }}>
              Nenhum dado de venda encontrado para o período selecionado
            </h3>
            <p style={{ margin: 0, fontSize: 13 }}>
              Altere o filtro de mês/ano ou selecione outro distribuidor no topo para visualizar as métricas.
            </p>
          </div>
        </ContentCard>
      )}

      {/* Modal Executivo de Exportação com Escolha de Distribuidor */}
      {openExportModal && (
        <ModalOverlay onClick={() => !exporting && setOpenExportModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <div className='header-info'>
                <div className='icon-badge'>
                  <FaFileExcel />
                </div>
                <div>
                  <h3>Exportar Planilha de SKUs & Batalha Naval</h3>
                  <p>Escolha o distribuidor para personalizar a extração executiva</p>
                </div>
              </div>
              <button
                type='button'
                className='close-btn'
                onClick={() => !exporting && setOpenExportModal(false)}
                disabled={exporting}
                title='Fechar'
              >
                <FaTimes />
              </button>
            </ModalHeader>

            <ModalBody>
              <div className='field-group'>
                <label htmlFor='dist-export-select'>Distribuidor para a Planilha</label>
                <select
                  id='dist-export-select'
                  value={exportDistId}
                  onChange={(e) => setExportDistId(e.target.value)}
                  disabled={exporting}
                >
                  <option value='todos'>Todos os Distribuidores (Visão Consolidada Nacional)</option>
                  {distributorsList.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name} {d.regional ? `(${d.regional})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className={`info-pill ${exportDistId === 'todos' ? 'nacional' : 'distribuidor'}`}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <FaCalendarAlt style={{ color: '#94a3b8' }} />
                  <span>
                    Período da Extração: <strong>{periodLabel}</strong>
                  </span>
                </div>
                {exportDistId === 'todos' ? (
                  <div>
                    <strong>Visão Nacional Consolidada:</strong> Os KPIs de faturamento, vendas médias, matriz de 4 quadrantes e sortimento serão consolidados com a totalidade dos distribuidores da operação.
                  </div>
                ) : (
                  <div>
                    <strong>Visão Individual do Distribuidor:</strong> A classificação da Batalha Naval (Q1 a Q4), sortimento de SKUs por PDV e ticket médio serão recalculados especificamente para a operação deste distribuidor.
                  </div>
                )}
              </div>

              <div className='tabs-preview'>
                <div className='preview-title'>Estrutura das 5 Abas Incluídas na Planilha</div>
                <div className='tabs-list'>
                  <div className='tab-item'>
                    <span className='bullet'>[1]</span> Resumo Executivo & Linhas de Corte
                  </div>
                  <div className='tab-item'>
                    <span className='bullet'>[2]</span> Batalha Naval (Categorias Q1-Q4)
                  </div>
                  <div className='tab-item'>
                    <span className='bullet'>[3]</span> Subcategorias & Famílias
                  </div>
                  <div className='tab-item'>
                    <span className='bullet'>[4]</span> Catálogo de SKUs (Pareto)
                  </div>
                  <div className='tab-item' style={{ gridColumn: 'span 2' }}>
                    <span className='bullet'>[5]</span> Mix de Sortimento por Distribuidor
                  </div>
                </div>
              </div>
            </ModalBody>

            <ModalFooter>
              <button
                type='button'
                className='btn-cancel'
                onClick={() => setOpenExportModal(false)}
                disabled={exporting}
              >
                Cancelar
              </button>
              <button
                type='button'
                className='btn-export'
                onClick={handleConfirmExport}
                disabled={exporting}
              >
                {exporting ? (
                  <>
                    <FaSpinner className='spin' />
                    Gerando Planilha...
                  </>
                ) : (
                  <>
                    <FaFileExcel />
                    Gerar e Baixar Planilha
                  </>
                )}
              </button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  )
}

